import { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../../context/authContext';
import { SearchContext } from '../../../../context/searchContext';
import { getItem, removeItem, STORAGE_KEYS } from '../../../../services/storage';
import {
  initialTypeByCentro,
  buildParamsByHeadquarter,
  addActionsToAlumnos,
} from '../utils';
import {
  useHeadquarterOptions,
  useFormationCenterOptions,
  useSearchFilterOptions,
} from './useDropdownQueries';
import useSearchAlumnos from './useSearchAlumnos';
import useTablaEstandar from '../../../tabla/useTablaEstandar';
import { COLUMNAS_ALUMNOS, TABLA_ALUMNOS } from '../columnasAlumnos';

const useFormularioUsuarios = () => {
  const navigate = useNavigate();
  const { searchContext, saveSearches } = useContext(SearchContext);
  const { userOU } = useContext(AuthContext);

  const [formationCenter, setFormationCenter] = useState('');
  const [phone, setPhone] = useState({ data: '', error: '' });
  const [year, setYear] = useState('All');
  const [headquarter, setHeadquarter] = useState('CEDETECGROUP');
  const [dni, setDni] = useState({ data: '', error: '' });
  const [email, setEmail] = useState({ data: '', error: '' });
  const [name, setName] = useState({ data: '', error: '' });
  const [idStripe, setIdStripe] = useState({ data: '', error: '' });
  const [lastname, setLastname] = useState({ data: '', error: '' });
  const [idUser, setIdUser] = useState({ data: '', error: '' });
  const [isFieldValid, setIsFieldValid] = useState(true);
  const [selectedIds, setSelectedIds] = useState('');
  const [cicle, setCicle] = useState('All');
  const [type, setType] = useState(() => initialTypeByCentro(userOU || {}));
  const [course, setCourse] = useState('All');
  const [deudores, setDeudores] = useState(false);
  const [searchParams, setSearchParams] = useState(null);

  // React Query hooks para opciones de dropdowns (cacheados 30 min)
  const headquarterQuery = useHeadquarterOptions();
  const formationQuery = useFormationCenterOptions();
  const filterOptionsQuery = useSearchFilterOptions();

  // Hook para búsqueda de alumnos con paginación progresiva (100 por tanda)
  // Estado de la tabla estándar (orden, filtros por columna, columnas, densidad) por usuario
  const tablaEstandar = useTablaEstandar({ tabla: TABLA_ALUMNOS, columnas: COLUMNAS_ALUMNOS });
  // Búsqueda rápida (caja de la barra): se aplica a los 300 ms de dejar de escribir.
  const [busquedaRapida, setBusquedaRapida] = useState('');
  const [busquedaAplicada, setBusquedaAplicada] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setBusquedaAplicada(busquedaRapida.trim()), 300);
    return () => clearTimeout(t);
  }, [busquedaRapida]);
  const consultaTabla = tablaEstandar.consulta;
  const consulta = useMemo(
    () => ({ ...consultaTabla, q: busquedaAplicada || undefined }),
    [consultaTabla, busquedaAplicada],
  );
  const searchQuery = useSearchAlumnos(consulta);

  // Datos derivados de React Query
  const headquarterList = headquarterQuery.data ?? '';
  const formationList = formationQuery.data ?? '';
  const typeList = filterOptionsQuery.data?.modalitat ?? '';
  const cicleList = filterOptionsQuery.data?.grau ?? '';
  const courseList = filterOptionsQuery.data?.cicle ?? '';
  const yearList = filterOptionsQuery.data?.curs ?? '';

  const hasDropdownError =
    headquarterQuery.isError ||
    formationQuery.isError ||
    filterOptionsQuery.isError ||
    filterOptionsQuery.data?.error === true;

  const isFormSubmittable = isFieldValid && !hasDropdownError;
  const isTableNotLoading = !searchQuery.isFetching;

  const handleStudentInfo = useCallback(
    (id, empresaId = '') => {
      const empresaLowerCase = empresaId.toLowerCase();
      if (getItem(STORAGE_KEYS.STUDENT)) removeItem(STORAGE_KEYS.STUDENT);
      navigate(`${empresaLowerCase}/${id}/detalles`);
    },
    [navigate],
  );

  const data = useMemo(() => {
    if (!searchQuery.data?.rows) return null;
    const rows = searchQuery.data.rows.map((row) => ({ ...row }));
    addActionsToAlumnos(rows, handleStudentInfo);
    return rows;
  }, [searchQuery.data, handleStudentInfo]);

  const searchMeta = useMemo(() => {
    if (!searchQuery.data) return null;
    return { total: searchQuery.data.total, hasMore: searchQuery.data.hasMore };
  }, [searchQuery.data]);

  // Ejecutar búsqueda cuando cambian los params
  const { search: executeSearch } = searchQuery;
  useEffect(() => {
    if (searchParams) executeSearch(searchParams);
  }, [searchParams, executeSearch]);

  const handleNavigateNuevoAlumno = () => {
    if (getItem(STORAGE_KEYS.STUDENT)) removeItem(STORAGE_KEYS.STUDENT);
    window.location.href = '/nuevo-alumno';
  };

  // Restaurar campos del formulario desde SearchContext y re-ejecutar búsqueda
  useEffect(() => {
    if (searchContext !== '' && searchContext.newSearch === false) {
      setName({ data: searchContext.nameSearch, error: '' });
      setLastname({ data: searchContext.lastnameSearch, error: '' });
      setEmail({ data: searchContext.emailSearch, error: '' });
      setDni({ data: searchContext.dniSearch, error: '' });
      setHeadquarter(searchContext.headquarterSearch);
      setIdUser({ data: searchContext.idUserSearch, error: '' });
      setIdStripe({ data: searchContext.idStripeSearch, error: '' });
      setYear(searchContext.yearSearch);
      setCicle(searchContext.courseSearch);
      setType(searchContext.typeSearch);
      setCourse(searchContext.cicleSearch);
      setPhone(searchContext.phoneSearch ?? { data: '', error: '' });
      setFormationCenter(searchContext.formationCenterSearch ?? '');
      setDeudores(searchContext.deudoresSearch ?? false);

      let delegacionesRestored = '';
      const fc = searchContext.formationCenterSearch;
      if (fc && (Array.isArray(fc) ? fc.length > 0 : true)) {
        const arr = Array.isArray(fc) ? fc : [fc];
        arr.forEach((opcion) => {
          const label = opcion?.label ?? opcion;
          delegacionesRestored += delegacionesRestored === '' ? `'${label}'` : `,'${label}'`;
        });
      }

      const restoredParams = buildParamsByHeadquarter({
        headquarter: searchContext.headquarterSearch,
        userOU,
        name: searchContext.nameSearch,
        lastname: searchContext.lastnameSearch,
        email: searchContext.emailSearch,
        dni: searchContext.dniSearch,
        year: searchContext.yearSearch,
        cicle: searchContext.cicleSearch,
        course: searchContext.courseSearch,
        type: searchContext.typeSearch,
        idUser: searchContext.idUserSearch,
        phone: searchContext.phoneSearch?.data ?? '',
        idStripe: searchContext.idStripeSearch ?? '',
        selectedIds: searchContext.documentacionSearch ?? selectedIds,
        delegaciones: delegacionesRestored,
        deudores: searchContext.deudoresSearch ?? false,
      });

      setSearchParams(restoredParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchContext]);

  const handleIDOnChange = (e) => {
    const value = e.target.value.trim();
    if (value.length === 0) {
      setIdUser({ data: '', error: '' });
      setIsFieldValid(true);
    } else if (value.match(/^[^0-9]*$/)) {
      setIdUser({ data: '', error: 'format' });
      setIsFieldValid(false);
    } else {
      setIdUser({ data: value, error: '' });
      setIsFieldValid(true);
    }
  };

  const handleNameOnChange = (e) => {
    const value = e.target.value.trim();
    if (value.length === 0) {
      setName({ data: '', error: '' });
      setIsFieldValid(true);
    } else if (!value.match(/^(?![\s.]+$)[a-zA-ZÀ-ÿ\s.]*$/)) {
      setName({ data: '', error: 'format' });
      setIsFieldValid(false);
    } else if (value.length === 1) {
      setName({ data: '', error: 'length' });
      setIsFieldValid(false);
    } else {
      setName({ data: value, error: '' });
      setIsFieldValid(true);
    }
  };

  const handleLastNameOnChange = (e) => {
    const value = e.target.value.trim();
    if (!value.match(/^(?![\s.]+$)[a-zA-ZÀ-ÿ\s.]*$/)) {
      setLastname({ data: '', error: 'format' });
      setIsFieldValid(false);
    } else if (value.length === 1) {
      setLastname({ data: '', error: 'length' });
      setIsFieldValid(false);
    } else {
      setLastname({ data: value, error: '' });
      setIsFieldValid(true);
    }
  };

  const handleDNIOnChange = (e) => {
    setDni({ data: e.target.value.trim(), error: '' });
    setIsFieldValid(true);
  };

  const handlePhoneOnChange = (e) => {
    const phon = e.target.value.trim();
    if (phon === '') {
      setPhone({ data: phon, error: '' });
      setIsFieldValid(true);
    } else {
      const isValidPhone = /^[67]\d{8}$/.test(phon);
      if (isValidPhone) {
        setPhone({ data: phon, error: '' });
        setIsFieldValid(true);
      } else {
        setPhone({ data: phon, error: 'El número debe comenzar con 7 o 6 y tener 9 dígitos.' });
        setIsFieldValid(false);
      }
    }
  };

  const handleEmailOnChange = (e) => {
    const value = e.target.value.trim();
    const emailRegex =
      /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
    if (value.length > 0 && !value.toLowerCase().match(emailRegex)) {
      setEmail({ data: '', error: 'format' });
      setIsFieldValid(false);
    } else {
      setEmail({ data: value, error: '' });
      setIsFieldValid(true);
    }
  };

  const handleHeadquarter = (e) => setHeadquarter(e.target.value);
  const handleFormationCenter = (selected) => setFormationCenter(selected);
  const handleCourse = (e) => setCourse(e.target.value);
  const handleYear = (e) => setYear(e.target.value);
  const handleType = (e) => setType(e.target.value);
  const handleCicle = (e) => setCicle(e.target.value);
  const handleDeudor = () => setDeudores(!deudores);

  const handleIdStripeOnChange = (e) => {
    const value = e.target.value.trim();
    if (value.length === 0) {
      setIdStripe({ data: '', error: '' });
    } else {
      setIdStripe({ data: e.target.value, error: '' });
    }
  };

  const handleReset = () => {
    setHeadquarter('CEDETECGROUP');
    setCicle('All');
    setDni({ data: '', error: '' });
    setName({ data: '', error: '' });
    setLastname({ data: '', error: '' });
    setCourse('All');
    setType(initialTypeByCentro(userOU));
    setYear('All');
    setEmail({ data: '', error: '' });
    setFormationCenter('');
    setDeudores(false);
    setPhone({ data: '', error: '' });
    setIdUser({ data: '', error: '' });
  };

  const handleOnSubmit = (e) => {
    e.preventDefault();

    let delegaciones = '';
    if (formationCenter && formationCenter !== '' && Array.isArray(formationCenter)) {
      formationCenter.forEach((opcion) => {
        delegaciones += delegaciones === '' ? `'${opcion.label}'` : `,'${opcion.label}'`;
      });
    }

    const newSearchParams = buildParamsByHeadquarter({
      headquarter,
      userOU,
      name: name.data,
      lastname: lastname.data,
      email: email.data,
      dni: dni.data,
      year,
      cicle,
      course,
      type,
      idUser: idUser.data,
      phone: phone.data,
      idStripe: idStripe.data,
      selectedIds,
      delegaciones,
      deudores,
    });

    setSearchParams(newSearchParams);

    saveSearches({
      nameSearch: name.data,
      lastnameSearch: lastname.data,
      dniSearch: dni.data,
      emailSearch: email.data,
      headquarterSearch: headquarter,
      courseSearch: course,
      typeSearch: type,
      cicleSearch: cicle,
      yearSearch: year,
      idUserSearch: idUser.data,
      formationCenterSearch: formationCenter,
      newSearch: true,
      phoneSearch: phone,
      deudores,
      idStripeSearch: idStripe.data,
      documentacionSearch: selectedIds,
    });
  };

  return {
    // Estado del formulario
    name,
    lastname,
    dni,
    idUser,
    email,
    phone,
    idStripe,
    headquarter,
    year,
    cicle,
    type,
    course,
    formationCenter,
    deudores,
    selectedIds,
    setSelectedIds,
    // Opciones (listas para selects)
    headquarterList,
    formationList,
    yearList,
    typeList,
    cicleList,
    courseList,
    // Estado UI
    data,
    searchMeta,
    isFormSubmittable,
    isTableNotLoading,
    isLoadingMore: searchQuery.isLoadingMore,
    loadMore: searchQuery.loadMore,
    tablaEstandar,
    busquedaRapida,
    setBusquedaRapida,
    loadFacets: searchQuery.loadFacets,
    exportar: searchQuery.exportar,
    isExporting: searchQuery.isExporting,
    exportError: searchQuery.exportError,
    userOU,
    searchContext,
    // Handlers
    handleNameOnChange,
    handleLastNameOnChange,
    handleDNIOnChange,
    handleIDOnChange,
    handleEmailOnChange,
    handlePhoneOnChange,
    handleHeadquarter,
    handleFormationCenter,
    handleYear,
    handleType,
    handleCicle,
    handleCourse,
    handleDeudor,
    handleIdStripeOnChange,
    handleReset,
    handleOnSubmit,
    handleNavigateNuevoAlumno,
  };
};

export default useFormularioUsuarios;
